// Split out of journey.ts (ADR-002 phase C): the observation drawer
// (open/close, dirty tracking, full re-render, comments, observation edits).

import { $ } from '../shared/dom';
import { showToast } from '../shared/ui';
import { state } from '../shared/state';
import { apiFetch } from '../api/client';
import {
  trajState,
  trajActions,
  stageConfig,
  isJourneyReadOnly,
  findObservationById,
  mergeObservationIntoJourney,
  escapeHtml,
} from './journey-state';
import {
  saveCycle,
  analyzeCycle,
  assistCycle,
  renderStage6Orientir,
  renderStage6MetaPanel,
  buildCycleContent,
  isCycleContentDirty,
  renderOfflineActivitiesSection,
} from './journey-cycles';
import type { JourneyObservation } from './journey.types';

async function promptEditObservation(obs: JourneyObservation) {
  if (!trajState.currentJourney) return;
  const title = prompt('Редактировать название наблюдения:', obs.title);
  if (!title) return;
  const description = prompt('Редактировать описание:', obs.description || '') || '';

  try {
    await apiFetch(`/api/journey-observations/${obs.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ title, description })
    });
    showToast('Наблюдение обновлено');
    await trajActions.reloadDetails(trajState.currentJourney.id);
  } catch (err: any) {
    showToast(err.message || 'Ошибка обновления наблюдения', 'error');
  }
}

async function deleteObservation(obsId: string) {
  if (!trajState.currentJourney) return;
  if (!confirm('Вы уверены, что хотите удалить это наблюдение? Все заполненные шаги будут утеряны.')) return;
  try {
    await apiFetch(`/api/journey-observations/${obsId}`, {
      method: 'DELETE'
    });
    showToast('Наблюдение удалено');
    closeObservationDrawer();
    await trajActions.reloadDetails(trajState.currentJourney.id);
  } catch (err: any) {
    showToast(err.message || 'Ошибка удаления наблюдения', 'error');
  }
}

export function openObservationDrawer(observationId: string) {
  trajState.selectedObservationId = observationId;
  const drawer = $('observation-drawer');
  const backdrop = $('observation-drawer-backdrop');
  if (drawer && backdrop) {
    drawer.classList.add('open');
    backdrop.classList.add('active');
    refreshDrawerContent();
  }
}

function isDrawerDirty(): boolean {
  const textareas = document.querySelectorAll<HTMLTextAreaElement>('#drawer-steps-list .step-textarea');
  for (const ta of textareas) {
    const original = ta.dataset.originalValue || '';
    if (ta.value.trim() !== original.trim()) {
      return true;
    }
  }
  return false;
}

async function saveAllDirtyCyclesAndClose() {
  const textareas = document.querySelectorAll<HTMLTextAreaElement>('#drawer-steps-list .step-textarea');
  const promises: Promise<void>[] = [];
  
  for (const ta of textareas) {
    const original = ta.dataset.originalValue || '';
    const stage = parseInt(ta.dataset.stage || '0', 10);
    const scale = (ta.dataset.scale === 'qualitative' ? 'qualitative' : 'daily') as 'daily' | 'qualitative';
    const stepBox = ta.closest('.step-box');
    if (!isCycleContentDirty(scale, stage, ta.value, (stepBox as HTMLElement) || ta, original)) continue;
    const obsId = ta.dataset.observationId;
    const cycleId = ta.dataset.cycleId || undefined;
    if (obsId) {
      promises.push(saveCycle(obsId, stage, cycleId, ta.value, true, scale, stepBox as HTMLElement | null));
    }
  }
  
  try {
    if (promises.length > 0) {
      await Promise.all(promises);
      showToast('Все изменения успешно сохранены');
    }
  } catch (err) {
    console.error('Failed to save dirty cycles on close', err);
  } finally {
    const drawer = $('observation-drawer');
    const backdrop = $('observation-drawer-backdrop');
    if (drawer && backdrop) {
      drawer.classList.remove('open');
      backdrop.classList.remove('active');
    }
    trajState.selectedObservationId = null;
    
    if (trajState.currentJourney) {
      await trajActions.reloadDetails(trajState.currentJourney.id);
    }
  }
}

export function closeObservationDrawer() {
  if (isDrawerDirty()) {
    const confirmClose = confirm('В цикле наблюдения есть несохраненные изменения. Сохранить их перед закрытием?');
    if (confirmClose) {
      saveAllDirtyCyclesAndClose();
      return;
    }
  }

  trajState.selectedObservationId = null;
  const drawer = $('observation-drawer');
  const backdrop = $('observation-drawer-backdrop');
  if (drawer && backdrop) {
    drawer.classList.remove('open');
    backdrop.classList.remove('active');
  }
}

async function ensureObservationCycles(obs: JourneyObservation): Promise<JourneyObservation> {
  if (obs.cycles && obs.cycles.length > 0) return obs;
  try {
    const loaded = await apiFetch<JourneyObservation>(`/api/journey-observations/${obs.id}`);
    mergeObservationIntoJourney(loaded);
    return loaded;
  } catch (err) {
    console.error('Failed to lazy-load observation cycles', err);
    return obs;
  }
}

export async function refreshDrawerContent() {
  if (!trajState.selectedObservationId) return;
  const match = findObservationById(trajState.selectedObservationId);
  if (!match) {
    closeObservationDrawer();
    return;
  }

  const { element } = match;
  const obs = await ensureObservationCycles(match.obs);
  const readOnly = trajState.currentJourney ? isJourneyReadOnly(trajState.currentJourney) : false;
  const role = trajState.currentJourney?.role || 'reader';
  const canEdit = (role === 'owner' || role === 'editor') && !readOnly;

  // Title info
  const titleDisplay = $('drawer-obs-title');
  if (titleDisplay) titleDisplay.textContent = obs.title;
  const sphereDisplay = $('drawer-obs-sphere');
  if (sphereDisplay) {
    const sphereNames: Record<string, string> = { work: 'Работа', relation: 'Отношения', project: 'Проекты' };
    sphereDisplay.textContent = `${sphereNames[element.category]} • ${element.title}`;
  }

  // Bind edit/delete observation buttons
  const editObsBtn = $('btn-edit-obs');
  if (editObsBtn) {
    editObsBtn.onclick = () => promptEditObservation(obs);
    editObsBtn.classList.toggle('hidden', !canEdit);
  }
  const deleteObsBtn = $('btn-delete-obs');
  if (deleteObsBtn) {
    deleteObsBtn.onclick = () => deleteObservation(obs.id);
    deleteObsBtn.classList.toggle('hidden', !canEdit);
  }

  // Load comments
  let comments: any[] = [];
  try {
    comments = await apiFetch<any[]>(`/api/journey-observations/${obs.id}/comments`) || [];
  } catch (err) {
    console.error('Failed to load comments', err);
  }

  // Render general comments
  const generalCommentsContainer = $('observation-general-comments');
  if (generalCommentsContainer) {
    renderCommentsSection(obs.id, null, comments.filter(c => c.stage_index === null), generalCommentsContainer, readOnly);
  }

  // Load suggestions (AI hypotheses)
  const suggestionsBlock = $('drawer-suggestions-block');
  const suggestionsList = $('drawer-suggestions-list');
  if (suggestionsBlock && suggestionsList && trajState.currentJourney) {
    try {
      const suggestions = await apiFetch<any[]>(`/api/journeys/${trajState.currentJourney.id}/suggestions`) || [];
      const obsSuggestions = suggestions.filter(s => s.observation_id === obs.id && s.status === 'pending');

      if (obsSuggestions.length === 0) {
        suggestionsBlock.classList.add('hidden');
        suggestionsList.innerHTML = '';
      } else {
        suggestionsBlock.classList.remove('hidden');
        suggestionsList.innerHTML = obsSuggestions.map(s => {
          const confidenceLabels: Record<string, string> = { high: 'Высокая', medium: 'Средняя', low: 'Низкая' };
          return `
            <div class="suggestion-card" id="suggestion-card-${s.id}">
              <div class="suggestion-header">
                <span class="suggestion-name">${escapeHtml(s.suggested_mechanism_name)}</span>
                <span class="suggestion-confidence suggestion-confidence-${s.confidence}">${confidenceLabels[s.confidence] || s.confidence} достоверность</span>
              </div>
              ${s.suggested_description ? `<div class="suggestion-desc">${escapeHtml(s.suggested_description)}</div>` : ''}
              ${canEdit ? `
                <div class="suggestion-actions">
                  <button type="button" class="btn-accept-suggestion" data-id="${s.id}">Подтвердить</button>
                  <button type="button" class="btn-reject-suggestion" data-id="${s.id}">Отклонить</button>
                </div>
              ` : ''}
            </div>
          `;
        }).join('');

        // Навешиваем обработчики на кнопки
        suggestionsList.querySelectorAll('.btn-accept-suggestion').forEach(btn => {
          btn.addEventListener('click', async () => {
            const sugId = btn.getAttribute('data-id');
            if (!sugId) return;
            try {
              btn.setAttribute('disabled', 'true');
              await apiFetch(`/api/journey-analysis-suggestions/${sugId}/accept`, { method: 'POST' });
              showToast('Механизм подтвержден');
              if (trajState.currentJourney) {
                await trajActions.reloadDetails(trajState.currentJourney.id);
              }
            } catch (err: any) {
              showToast(err.message || 'Ошибка подтверждения предложения', 'error');
              btn.removeAttribute('disabled');
            }
          });
        });

        suggestionsList.querySelectorAll('.btn-reject-suggestion').forEach(btn => {
          btn.addEventListener('click', async () => {
            const sugId = btn.getAttribute('data-id');
            if (!sugId) return;
            try {
              btn.setAttribute('disabled', 'true');
              await apiFetch(`/api/journey-analysis-suggestions/${sugId}/reject`, { method: 'POST' });
              showToast('Предложение отклонено');
              if (trajState.currentJourney) {
                await trajActions.reloadDetails(trajState.currentJourney.id);
              }
            } catch (err: any) {
              showToast(err.message || 'Ошибка отклонения предложения', 'error');
              btn.removeAttribute('disabled');
            }
          });
        });
      }
    } catch (err) {
      console.error('Failed to load AI suggestions', err);
      suggestionsBlock.classList.add('hidden');
    }
  }

  const container = $('drawer-steps-list');
  if (!container) return;
  container.innerHTML = '';

  const renderScaleBlock = (
    scale: 'daily' | 'qualitative',
    sectionTitle: string,
    names: string[],
    descs: string[]
  ) => {
    const section = document.createElement('div');
    section.className = 'drawer-scale-section';
    section.dataset.scale = scale;
    section.innerHTML = `<h3 class="drawer-scale-title">${escapeHtml(sectionTitle)}</h3>`;
    container.appendChild(section);
    if (scale === 'qualitative' && names.length > 6) {
      renderStage6Orientir(section);
    }

    for (let stage = 0; stage < names.length; stage++) {
      const cycle = obs.cycles?.find(
        (s: any) => s.stage_index === stage && s.is_current && (s.scale || 'qualitative') === scale
      );
      let textValue = cycle?.content?.text || '';
      let isSaving = false;

      const stepBox = document.createElement('div');
      stepBox.className = 'step-box';
      stepBox.innerHTML = `
      <div class="step-box-header">
        <div>
          <div class="step-title">${escapeHtml(names[stage])}</div>
          <div class="step-desc">${escapeHtml(descs[stage] || '')}</div>
        </div>
      </div>
      <textarea class="step-textarea" 
                data-scale="${scale}"
                data-stage="${stage}" 
                data-observation-id="${obs.id}" 
                data-cycle-id="${cycle?.id || ''}" 
                placeholder="Опишите ваши наблюдения..." 
                ${!canEdit ? 'disabled' : ''}>${escapeHtml(textValue)}</textarea>
      ${canEdit ? `
        <div class="step-actions">
          <button class="btn-primary-sm save-step-btn" disabled>💾 Сохранить</button>
          <button class="btn-secondary-sm assist-step-btn">🪄 AI Подсказать</button>
          ${scale === 'qualitative' && stage >= 1 ? `<button class="btn-secondary-sm analyze-step-btn">🔍 Анализ ЭР</button>` : ''}
        </div>
      ` : ''}
      <div class="step-assistant-area" id="assistant-${scale}-stage-${stage}"></div>
      <div class="step-analysis-area" id="analysis-${scale}-stage-${stage}"></div>
      ${scale === 'qualitative' ? `<div class="step-comments-area" id="comments-stage-${stage}"></div>` : ''}
    `;
      section.appendChild(stepBox);
      if (scale === 'qualitative' && stage === 6) {
        renderStage6MetaPanel(stepBox, cycle, canEdit);
        stepBox.dataset.contentBaseline = JSON.stringify(buildCycleContent(scale, stage, textValue, stepBox));
      }

      const textarea = stepBox.querySelector<HTMLTextAreaElement>('.step-textarea')!;
      textarea.dataset.originalValue = textValue;
      const saveBtn = stepBox.querySelector<HTMLButtonElement>('.save-step-btn');
      const assistBtn = stepBox.querySelector<HTMLButtonElement>('.assist-step-btn');
      const analyzeBtn = stepBox.querySelector<HTMLButtonElement>('.analyze-step-btn');
      const assistantArea = stepBox.querySelector<HTMLElement>('.step-assistant-area')!;
      const analysisArea = stepBox.querySelector<HTMLElement>('.step-analysis-area')!;

      const markDirty = () => {
        const dirty = isCycleContentDirty(scale, stage, textarea.value, stepBox, textValue);
        if (saveBtn) saveBtn.disabled = !dirty;
      };

      textarea.addEventListener('input', markDirty);
      if (scale === 'qualitative' && stage === 6) {
        stepBox.querySelectorAll<HTMLInputElement | HTMLSelectElement>('.stage6-input, .stage6-distortion-checks input').forEach((el) => {
          el.addEventListener('input', markDirty);
          el.addEventListener('change', markDirty);
        });
      }

      textarea.addEventListener('blur', async () => {
        const currentVal = textarea.value;
        if (isCycleContentDirty(scale, stage, currentVal, stepBox, textValue) && !isSaving) {
          isSaving = true;
          if (saveBtn) {
            saveBtn.textContent = '💾 Сохранение...';
            saveBtn.disabled = true;
          }
          try {
            const latestCycle = obs.cycles?.find(
              (s: any) => s.stage_index === stage && s.is_current && (s.scale || 'qualitative') === scale
            );
            await saveCycle(obs.id, stage, latestCycle?.id, currentVal, true, scale, stepBox);
            textValue = currentVal;
            textarea.dataset.originalValue = currentVal;
            if (scale === 'qualitative' && stage === 6) {
              stepBox.dataset.contentBaseline = JSON.stringify(buildCycleContent(scale, stage, currentVal, stepBox));
            }
          } finally {
            isSaving = false;
            if (saveBtn) {
              saveBtn.textContent = '💾 Сохранить';
              saveBtn.disabled = true;
            }
          }
        }
      });

      saveBtn?.addEventListener('click', async () => {
        const latestCycle = obs.cycles?.find(
          (s: any) => s.stage_index === stage && s.is_current && (s.scale || 'qualitative') === scale
        );
        await saveCycle(obs.id, stage, latestCycle?.id, textarea.value, false, scale, stepBox);
      });

      assistBtn?.addEventListener('click', async () => {
        await assistCycle(obs.id, stage, textarea, assistantArea, assistBtn);
      });

      analyzeBtn?.addEventListener('click', async () => {
        const currentVal = textarea.value.trim();
        if (!currentVal) {
          showToast('Пожалуйста, напишите наблюдения перед анализом', 'warning');
          return;
        }
        const currentCycle = obs.cycles?.find(
          (s: any) => s.stage_index === stage && s.is_current && (s.scale || 'qualitative') === scale
        );
        if (currentCycle?.id) {
          await analyzeCycle(currentCycle.id, analysisArea, analyzeBtn);
        }
      });

      if (scale === 'qualitative') {
        const commentsArea = stepBox.querySelector<HTMLElement>('.step-comments-area');
        if (commentsArea) {
          renderCommentsSection(obs.id, stage, comments.filter(c => c.stage_index === stage), commentsArea, readOnly);
        }
      }
    }

    if (scale === 'qualitative' && names.length > 6) {
      const stage6Cycle = obs.cycles?.find(
        (s: any) => s.stage_index === 6 && s.is_current && (s.scale || 'qualitative') === 'qualitative'
      );
      void renderOfflineActivitiesSection(obs.id, section, canEdit, stage6Cycle?.id);
    }
  };

  renderScaleBlock('daily', 'Ежедневный цикл события', stageConfig.STAGE_NAMES, stageConfig.STAGE_DESCS);
  renderScaleBlock('qualitative', 'Качественная динамика механизма', stageConfig.QUALITATIVE_STAGE_NAMES, stageConfig.QUALITATIVE_STAGE_DESCS);
}

function renderCommentsSection(observationId: string, stageIndex: number | null, items: any[], container: HTMLElement, readOnly: boolean) {
  const title = stageIndex === null ? '💬 Общие комментарии' : '💬 Комментарии к этапу';
  
  const commentsHtml = items.map(c => {
    const isOwner = trajState.currentJourney?.role === 'owner';
    const isAuthor = state.currentUser?.username === c.username;
    const isAdmin = !!state.currentUser?.is_admin;
    const canDelete = isOwner || isAuthor || isAdmin;
    
    const formattedDate = new Date(c.created_at).toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });

    return `
      <div class="comment-item" data-id="${c.id}" style="margin-top: 6px;">
        <div class="comment-header" style="display: flex; justify-content: space-between; font-size: 0.75rem; color: var(--muted); margin-bottom: 4px;">
          <div>
            <strong class="comment-author" style="color: var(--text);">${escapeHtml(c.username)}</strong>
            <span class="comment-date" style="margin-left: 6px;">${formattedDate}</span>
          </div>
          ${canDelete ? `<button class="comment-delete-btn" data-id="${c.id}" style="background: none; border: none; color: var(--muted); cursor: pointer; font-size: 0.75rem; padding: 0 4px;">Удалить</button>` : ''}
        </div>
        <div class="comment-text" style="color: var(--text); line-height: 1.4; white-space: pre-wrap;">${escapeHtml(c.content)}</div>
      </div>
    `;
  }).join('');

  const titleHtml = items.length > 0 ? `
    <div class="comments-title" style="font-size: 0.8rem; font-weight: 600; color: var(--muted); margin-bottom: 6px; margin-top: 12px; border-top: 1px dashed var(--border); padding-top: 8px;">${title} (${items.length})</div>
  ` : '';

  const listHtml = items.length > 0 ? `
    <div class="comments-list" style="display: flex; flex-direction: column; gap: 6px; margin-bottom: 8px;">
      ${commentsHtml}
    </div>
  ` : '';

  container.innerHTML = `
    ${titleHtml}
    ${listHtml}
    ${!readOnly ? `
      <form class="comment-form" style="display: flex; gap: 8px; margin-top: 6px; ${items.length === 0 ? 'border-top: 1px dashed var(--border); padding-top: 8px; margin-top: 12px;' : ''}">
        <input type="text" class="comment-input" placeholder="Напишите комментарий..." required autocomplete="off" style="flex: 1; background: var(--bg-input); color: var(--text); border: 1px solid var(--border); border-radius: 4px; padding: 4px 8px; font-size: 0.8rem; height: 30px; outline: none;">
        <button type="submit" class="comment-submit-btn" style="background: var(--accent); color: #fff; border: none; border-radius: 4px; padding: 0 10px; font-size: 0.8rem; cursor: pointer; height: 30px;">Отправить</button>
      </form>
    ` : ''}
  `;

  // Bind delete events
  container.querySelectorAll('.comment-delete-btn').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const commentId = (e.currentTarget as HTMLElement).dataset.id;
      if (commentId && confirm('Вы уверены, что хотите удалить этот комментарий?')) {
        try {
          await apiFetch(`/api/journey-comments/${commentId}`, { method: 'DELETE' });
          showToast('Комментарий удален');
          refreshDrawerContent();
        } catch (err: any) {
          showToast(err.message || 'Ошибка удаления комментария', 'error');
        }
      }
    });
  });

  // Bind submit events
  const form = container.querySelector<HTMLFormElement>('.comment-form');
  form?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const input = form.querySelector<HTMLInputElement>('.comment-input')!;
    const content = input.value.trim();
    if (!content) return;

    try {
      await apiFetch(`/api/journey-observations/${observationId}/comments`, {
        method: 'POST',
        body: JSON.stringify({ stage_index: stageIndex, content })
      });
      input.value = '';
      showToast('Комментарий добавлен');
      refreshDrawerContent();
    } catch (err: any) {
      showToast(err.message || 'Ошибка добавления комментария', 'error');
    }
  });
}
